import get from "lodash/get";
import React, { useEffect, useState } from "react";
import { Button, DownloadIcon, Modal } from "@pd/pd-ui-kit";
import { endOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";

import BlockQ from "~/pages/Main/BlockQ";
import Wrapper from "~/shared/ui/Wrapper";
import { DealerInfoPanel } from "~/entity/Dealer";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getIndicatorValue } from "~/libs/getData";
import { getIndicatorPeriod } from "~/libs/getFormatDate";
import {
  QuartilesAvailable,
  useGetAllowedMonthsForDealerLazyQuery,
  useGetQBeautifulReportsLazyQuery,
  useGetQFullReportsLazyQuery,
  useGetQRatingDataLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";
import { TableV2 } from "~/shared/ui/TableV2";

import QView from "./QView";
import QuartileWithChartView from "./QWithChartView";
import Actions from "./ui/Actions";
import { newFormat } from "./formater";
import { getQColumns } from "./model/tableAdapter";
import { Q_COLOR_OPTIONS, Q_OPTIONS } from "./options";
import { QFilter } from "./ui/QFilter";

import s from "./Q.less";

export interface IQExcludedDatesList {
  [key: number]: number[];
}

const Q = () => {
  const { t } = useTranslation();
  const lang = localStorage.getItem("language");
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();
  const currentDealerId = useCurrentDealerId();
  const noCaching = sessionStorage.getItem("noCaching");
  const { selectedDate, openChildModal, openModal } = useGetLocationData();
  const [currentDate, setCurrentDate] = useState<Date>();

  const { notifyReport } = useNotify();
  const [getQFullReportsLazyQuery, QFullReportsLazyQuery] =
    useGetQFullReportsLazyQuery({
      onCompleted(data) {
        const hash = get(data, "QuartilesRatingGetter.getFullReports.fileHash");
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });
  const [getQBeautifulReportsLazyQuery, QBeautifulReportsLazyQuery] =
    useGetQBeautifulReportsLazyQuery({
      onCompleted(data) {
        const hash = get(
          data,
          "QuartilesRatingGetter.getBeautifulReports.fileHash",
        );
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });

  /** Список доступных для выбора дат, для выбора квартилей */
  const [allowDates, setAllowDates] = useState<IQExcludedDatesList>([]);
  const [getQDateExcludes, getQDateExcludesData] =
    useGetAllowedMonthsForDealerLazyQuery();
  useEffect(() => {
    getQDateExcludes({
      variables: {
        dealerId: currentDealerId,
      },
    });
  }, []);

  /** Обработчик getQDateExcludes (доступных для выбора дат, для выбора квартилей).
   *
   * Преобразовываем getQDateExcludes в оптимизированный список и
   * выставляем начальное значение currentDate
   */
  useEffect(() => {
    if (!getQDateExcludesData.loading && getQDateExcludesData.data) {
      const allowDatesRawList = get(
        getQDateExcludesData.data,
        "QuartilesRatingGetter.getAllowedMonthsForDealer.items",
        [],
      ) as QuartilesAvailable[];
      const getLastDate = allowDatesRawList[allowDatesRawList.length - 1];

      setCurrentDate(
        getLastDate
          ? endOfMonth(new Date(`${getLastDate.year}.${getLastDate.month}`))
          : null,
      );

      const allowDatesReorganizeList: IQExcludedDatesList =
        allowDatesRawList.reduce((acc, dateElement) => {
          return acc[dateElement.year]
            ? {
                ...acc,
                [dateElement.year]: [
                  ...acc[dateElement.year],
                  dateElement.month,
                ],
              }
            : { ...acc, [dateElement.year]: [dateElement.month] };
        }, {});

      setAllowDates(allowDatesReorganizeList);
    }
  }, [getQDateExcludesData.loading, getQDateExcludesData.data]);

  /** Запрос квартилей */
  const [getQRating, { data, loading, error }] = useGetQRatingDataLazyQuery({
    fetchPolicy: noCaching ? "no-cache" : "cache-first",
  });

  useEffect(() => {
    if (!getQDateExcludesData.loading && currentDate) {
      getQRating({
        variables: {
          DealerId: currentDealerId,
          date: newFormat(selectedDate || currentDate),
        },
      });
    }
  }, [
    currentDate,
    selectedDate,
    getQDateExcludesData.loading,
    currentDealerId,
  ]);

  const qRatings = error
    ? []
    : get(data, "QuartilesRatingGetter.getRatingData.Ratings", []).map(
        ({
          KPIen,
          KPIru,
          LastPeriodIndicator,
          ValueIndicator,
          GroupIndicator,
          AverageIndicator,
          DifferenceIndicator,
          GroupAverageIndicator,
          round,
          format,
          ...qR
        }) => {
          const LastPeriodIndicatorValues =
            get(LastPeriodIndicator, "Values") || [];
          const lastPeriodIndicator = {
            createdAt: get(
              LastPeriodIndicatorValues[LastPeriodIndicatorValues.length - 1],
              "createdAt",
            ),
            period: get(LastPeriodIndicator, "period"),
          };

          return {
            ...qR,
            KPI: lang === "en" ? KPIen : KPIru,
            LastPeriodIndicatorId: lastPeriodIndicator.createdAt
              ? getIndicatorPeriod(lastPeriodIndicator)
              : "-",
            ValueIndicatorId: getIndicatorValue(ValueIndicator, round, format),
            GroupIndicatorId: getIndicatorValue(GroupIndicator),
            AverageIndicatorId: getIndicatorValue(
              AverageIndicator,
              round,
              format,
            ),
            DifferenceIndicatorId: getIndicatorValue(
              DifferenceIndicator,
              round,
              format,
            ),
            GroupAverageIndicatorId: getIndicatorValue(
              GroupAverageIndicator,
              round,
              format,
            ),
            button: <Actions record={qR} />,
          };
        },
      );

  const columns = getQColumns(t, Q_OPTIONS, Q_COLOR_OPTIONS);

  const getQFullReports = () => {
    getQFullReportsLazyQuery({
      variables: {
        DealerId: currentDealerId,
        date: currentDate,
        lang,
      },
    });
  };

  const getQBeautifulReports = () => {
    getQBeautifulReportsLazyQuery({
      variables: {
        DealerId: currentDealerId,
        date: currentDate,
        lang,
      },
    });
  };

  const openViewModal = () => {
    setDataReplace("openModal", "Quartiles");
  };

  const handleCloseChildModal = () => {
    setDataReplace("openChildModal", "");
  };
  const handleCloseModal = () => {
    setDataReplace("openModal", "");
  };

  if (!currentDate && !getQDateExcludesData.loading)
    return (
      <div className={s.fallbackView}>
        <p>Нет доступных квартилей</p>
      </div>
    );

  return (
    <Wrapper.Content
      title={t("q.quartiles")}
      filterComponent={
        <div className={s.filterArea}>
          <DealerInfoPanel />
          <QFilter currentDate={currentDate} allowDates={allowDates} />
        </div>
      }
    >
      <div className={s.actionsRow}>
        <div className={s.actionsGroup}>
          {hasPermission("quartilesRatings.beautifulReports.get") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size="middle"
              radius={30}
              icon={<DownloadIcon size={16} fill="#495a64" />}
              onClick={getQBeautifulReports}
              loading={QBeautifulReportsLazyQuery.loading}
            >
              {t("q.save_excel")}
            </Button>
          )}
          {hasPermission("quartilesRatings.fullReports.get") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size="middle"
              radius={30}
              icon={<DownloadIcon size={16} fill="#495a64" />}
              onClick={getQFullReports}
              loading={QFullReportsLazyQuery.loading}
            >
              {t("q.full_excel")}
            </Button>
          )}
        </div>
        {hasPermission("quartilesRatings.detailingMainIndicator.get") && (
          <BlockQ
            onClick={openViewModal}
            date={
              selectedDate && selectedDate?.length ? selectedDate : currentDate
            }
          />
        )}
      </div>
      <TableV2
        columns={columns}
        dataSource={qRatings}
        loading={loading}
        wrapperClassName={s.q_table}
        pagination={false}
      />

      <Modal
        open={openChildModal === "View"}
        onCancel={handleCloseChildModal}
        size={"large"}
        centered
        closable
      >
        <QuartileWithChartView
          currentDate={
            selectedDate && selectedDate?.length ? selectedDate : currentDate
          }
        />
      </Modal>

      <Modal
        open={openModal === "Quartiles"}
        onCancel={handleCloseModal}
        centered
        closable
      >
        <QView
          currentDate={
            selectedDate && selectedDate?.length ? selectedDate : currentDate
          }
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default Q;
